import axios from "axios";

const TOKEN_KEY = "lk_auth_token";
const USER_KEY = "lk-user";

/**
 * Read the token fresh on every request.
 *
 * This used to be captured once at module load, so a token written after the
 * bundle booted — logging in mid-session, or logging out and back in — was
 * never picked up, and requests kept going out with whatever the page started
 * with. The UI still showed the user as signed in, so a seat hold came back
 * 401 with no way to recover short of a reload.
 *
 * `localStorage` also hands back the *string* "undefined" if a login path ever
 * stored a missing value. That is truthy, so it would ship as
 * `Bearer undefined` and read as a 401 rather than as a missing token.
 */
const readToken = () => {
  try {
    const raw = localStorage.getItem(TOKEN_KEY);
    if (!raw || raw === "undefined" || raw === "null") return null;
    return raw;
  } catch {
    return null;
  }
};

/**
 * Give an in-flight login a moment to land, but bounded — the previous version
 * awaited a promise that only ever resolved once a token appeared, so a request
 * made while logged out hung indefinitely instead of failing.
 */
const awaitToken = async (timeoutMs = 3000, stepMs = 100) => {
  const deadline = Date.now() + timeoutMs;
  let token = readToken();
  while (!token && Date.now() < deadline) {
    await new Promise((resolve) => setTimeout(resolve, stepMs));
    token = readToken();
  }
  return token;
};

const axiosUser = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
  headers: { "Content-Type": "application/json" },
});

axiosUser.interceptors.request.use(
  async (config) => {
    if (config.headers["Authorization"]) return config;
    const token = await awaitToken();
    if (token) config.headers["Authorization"] = `Bearer ${token}`;
    return config;
  },
  (error) => Promise.reject(error)
);

// A 401 means the stored credential is not accepted. Dropping it keeps the UI
// from insisting the user is signed in with a token the API rejects; the event
// lets AuthContext reopen the login modal so the flow is recoverable in place
// instead of dead-ending on "Unauthorized".
axiosUser.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error?.response?.status === 401) {
      try {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
      } catch {
        // storage unavailable — the event below still prompts a fresh login
      }
      window.dispatchEvent(new CustomEvent("lk:unauthorized"));
    }
    return Promise.reject(error);
  }
);

export { axiosUser, readToken, TOKEN_KEY, USER_KEY };
